import { LayoutPositionEnum } from './types';

/**
 * Returns the correct height for given module and state
 *
 * @param module {'RECORD' | 'LINE_ITEMS'} module to add style
 * @param currentState {LayoutPositionEnum} current state of layout
 * @param isVH {boolean} if true, returns height in vh, if false, returns height in %
 */
export const getLayoutPositionHeight = (module: 'RECORD' | 'ADDITIONAL_INFO', currentState: LayoutPositionEnum, isVH: boolean = false) => {
    let result;
    switch (currentState) {
        case 'ONLY_ADDITIONAL_INFO': {
            if (module === 'ADDITIONAL_INFO') {
                result = isVH ? '100vh' : '100%';
            } else {
                result = 0;
            }
            break;
        }
        case 'SPLIT_VIEW': {
            result = isVH ? '50vh' : '50%';
            break;
        }
        default: {
            if (module === 'ADDITIONAL_INFO') {
                result = 0;
            } else {
                result = isVH ? '100vh' : '100%';
            }
            break;
        }
    }
    return result;
};

/**
 * Extracts the text value from the first block of a JSON string.
 *
 * @param json - A JSON string expected to contain an object with a 'blocks' array.
 * @returns The text value from the first block if available, otherwise an error message.
 */
export const extractTextFromJson = (json: string, valueOnEmpty: string) => {
    if (!json) {
        return valueOnEmpty;
    }

    try {
        // Parse the JSON string
        const parsedData = JSON.parse(json);

        // Ensure parsedData is an object and has blocks array
        if (typeof parsedData !== 'object' || !Array.isArray(parsedData.blocks)) {
            return json || valueOnEmpty;
        }

        // Extract the text value from the first block
        const textValue = parsedData.blocks[0]?.text || json || valueOnEmpty;

        return textValue;
    } catch (error) {
        return json ?? valueOnEmpty;
    }
};

export const emptyRequiredFieldsErrorMessage = (dataType: string) => {
    switch (dataType) {
        case 'attachment':
            return 'You must upload a file.';
        case 'object':
            return 'This field cannot be empty.';
        case 'chips-multiselect':
            return 'Please select an option.';
        case 'toggle':
            return 'This field to be set to Yes or No.';
        case 'multiselect-checkbox':
            return 'Select at least one option.';
        case 'multiselect':
            return 'Select at least one option.';
        case 'checkbox':
            return 'You must check this box to proceed.';
        case 'single-select':
            return 'Please select an option.';
        case 'dropdown':
            return 'Please select an option.';
        case 'url':
            return 'A valid URL is required.';
        case 'email':
            return 'A valid email is required.';
        case 'phone':
            return 'A valid phone number is required.';
        case 'date':
            return 'A date is required.';
        case 'datetime':
            return 'A date is required.';
        case 'time':
            return 'A valid time is required.';
        case 'number':
            return 'A valid number is required.';
        case 'currency':
            return 'A valid currency is required.';
        case 'percentage':
            return 'A valid percentage is required.';
        case 'textarea':
            return 'This field cannot be empty.';

        default:
            return 'This field is required.';
    }
};
